import { useChartMarkers } from '@/modules/imports/container/scan-discs-popup-container/editor/useChartMarkers'
import * as React from 'react'
import {
  ActivityType,
  ActivityTypeId,
  ChartScanDisc,
  UpdateDiscConflict,
  UpdateDiscPayload,
  UpdateDiscResponse,
  fetchDiscCanonicalImageObjectUrl,
  getDriverDayNote,
  redetectChartDisc,
} from '@/lib/api/company/chartScan'
import { TachographChartCanvas, parseChartIso } from '@/modules/imports/components/TachographChartCanvas'
import { discToEditorState, useChartEditor } from './useChartEditor'
import { ChartFormSidebar } from './ChartFormSidebar'
import { ViolationsExpander } from './ViolationsExpander'
import { ChartSummary } from './ChartSummary'
import { ChartImportToolbar } from './ChartImportToolbar'
import { ActivityListTable } from './ActivityListTable'
import { useDiscContextMenu } from './useDiscContextMenu'
import { clockMinute } from './chartDay'
import { ChartEditorFooter } from './ChartEditorFooter'
import { useChartEditorShortcuts } from './useChartEditorShortcuts'
import { DiagnosticPanel } from './DiagnosticPanel'
import { fmt, useChartT } from '../i18n'
import { ConflictDialog } from './ConflictDialog'

type Props = {
  companyId: string
  disc: ChartScanDisc
  onSave: (payload: UpdateDiscPayload) => Promise<UpdateDiscResponse>
  onDelete: () => Promise<void>
  onCancel: () => void
  onPrev?: () => void
  onNext?: () => void
  hasPrev: boolean
  hasNext: boolean
  discIndex: number
  discCount: number
  // Re-open the scan-level assign step from the editor (1:1 with WPF, where
  // sidebar Driver/Vehicle/Date fields are always reachable from the import
  // view).
  onReassign?: () => void
}

export function ChartEditorLayout({
  companyId,
  disc,
  onSave,
  onDelete,
  onCancel,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  discIndex,
  discCount,
  onReassign,
}: Props) {
  const editor = useChartEditor(disc)
  const contextMenu = useDiscContextMenu(companyId, disc.discId, editor)
  // Locale-aware string lookups; missing keys fall back to the literal Polish text.
  const T = useChartT()
  // No mode selected by default — matches WPF, where the user must click an
  // activity-type button before clicking on the chart adds anything.
  const [addingMode, setAddingMode] = React.useState<ActivityTypeId | null>(null)
  const [zoom, setZoom] = React.useState(1)
  const [showDistance, setShowDistance] = React.useState(false)
  // Default = wide view (chart in center with padding). Matches WPF's default
  // when ShowNotClippedTachographChart configuration is ON. User can toggle to
  // compact view (chart fills the canvas) via the toolbar Expand/Compress icon.
  const [compactView, setCompactView] = React.useState(false)
  const [cursorMinute, setCursorMinute] = React.useState<number | null>(null)
  const [imageUrl, setImageUrl] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)
  // When true, the per-minute diagnostic overlay is open.
  const [diagnosticOpen, setDiagnosticOpen] = React.useState(false)

  const markers = useChartMarkers(companyId, editor)

  // When the selected driver/date changes, fetch the per-day note from
  // DriverDays. Don't clobber the user's local edits — only prefill when the
  // current dayNote hasn't been touched.
  const lastFetchKey = React.useRef<string>('')
  React.useEffect(() => {
    if (editor.driverId == null) return
    const date = editor.dayStartDate.slice(0, 10)
    const key = `${editor.driverId}|${date}`
    if (key === lastFetchKey.current) return
    lastFetchKey.current = key
    if (editor.dayNote && editor.dayNote.length > 0) return
    getDriverDayNote(companyId, editor.driverId, date)
      .then((r) => {
        if (r.data.note) editor.setDayNote(r.data.note)
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.driverId, editor.dayStartDate])

  // Measure the canvas wrapper so the Konva Stage fills available space (mirrors
  // WPF's TachographChartTrimmedSizeConverter that resized the chart to the
  // viewport's smaller dimension minus padding).
  const canvasWrapRef = React.useRef<HTMLDivElement>(null)
  const [canvasSize, setCanvasSize] = React.useState(700)
  React.useEffect(() => {
    if (!canvasWrapRef.current) return
    const ro = new ResizeObserver((entries) => {
      const e = entries[0]
      if (!e) return
      const { width, height } = e.contentRect
      // The chart is intrinsically square (the disc has fixed aspect ratio).
      // Use the smaller of width/height so it fits both ways. Reserve only a
      // few pixels for the hour-label baseline; the labels are already drawn
      // inside the canvas area (they sit at outerR + 26 from center, but the
      // disc itself is only outerR — the difference fits without extra padding).
      setCanvasSize(Math.max(400, Math.min(width, height) - 4))
    })
    ro.observe(canvasWrapRef.current)
    return () => ro.disconnect()
  }, [])

  // Fetch the canonical 800×800 PNG when the disc changes.
  React.useEffect(() => {
    let revoked = false
    let url: string | null = null
    setImageUrl(null)
    fetchDiscCanonicalImageObjectUrl(companyId, disc.discId).then((u) => {
      if (revoked) URL.revokeObjectURL(u)
      else {
        url = u
        setImageUrl(u)
      }
    })
    return () => {
      revoked = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [companyId, disc.discId])

  // Auto-redetect when a disc opens with no activities. Activities can't be
  // persisted without a driver (TachographChartActivityEntity.DriverEntityId is
  // a non-null FK), so a disc assigned with no driver comes back from the server
  // with an empty list. Re-run the chart reader on load — same mechanism as the
  // toolbar's manual refresh — so the auto-detected activities are shown for
  // review regardless. The real persist still happens on save, which requires a
  // driver. Skipped when the disc already has activities (don't clobber a save).
  const autoRedetectedDiscRef = React.useRef<number | null>(null)
  React.useEffect(() => {
    if (disc.activities.length > 0) return
    if (autoRedetectedDiscRef.current === disc.discId) return
    autoRedetectedDiscRef.current = disc.discId
    let cancelled = false
    redetectChartDisc(companyId, disc.discId)
      .then((r) => {
        if (cancelled || r.data.length === 0) return
        // Re-seed the baseline from this disc + the detected activities rather
        // than `replaceActivities`: the latter closes over the editor state from
        // this render, which (mid-navigation) can still be the previous disc's
        // metadata. `discToEditorState(disc)` rebuilds cleanly from the current
        // disc prop, so we never write a stale driver/date. No undo artifact.
        editor.reset({ ...discToEditorState(disc), activities: r.data })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, disc.discId])

  // Day-start / day-end as minutes from the day's 00:00 (mirrors WPF
  // ShiftedBoundaryTime).
  const dayStartMin = isoToDayMinute(editor.dayStartDate)
  const dayEndMin = isoToDayMinute(editor.dayEndDate)

  useChartEditorShortcuts(!contextMenu.isInteracting, {
    addingMode,
    onSetMode: setAddingMode,
    onDeleteSelected: editor.deleteSelected,
    onDeleteAllOfType: editor.deleteAllOfType,
    onUndo: editor.undo,
    onRedo: editor.redo,
  })

  // Pre-save validation: driver + vehicle are required, every activity's
  // start ≤ end, activities are contiguous (no gaps). Issues surface inline
  // and the Save button gates on `errors.length === 0`.
  const errors = React.useMemo<string[]>(() => {
    const out: string[] = []
    if (editor.driverId == null) out.push(T('errMissingDriver', 'Wybierz kierowcę.'))
    if (editor.vehicleId == null) out.push(T('errMissingVehicle', 'Wybierz pojazd.'))
    // Clock times: an activity may cross midnight anywhere in the list (the day
    // can start in the evening), so only a zero-length one is wrong.
    const acts = editor.activities
    for (let i = 0; i < acts.length; i++) {
      const s = clockMinute(acts[i].startTime)
      if (s === clockMinute(acts[i].endTime) && acts.length > 1) {
        out.push(fmt(T('errActivityEndLeStart', 'Aktywność {0}: koniec ≤ początek.'), i + 1))
        break
      }
      if (i > 0 && clockMinute(acts[i - 1].endTime) !== s) {
        out.push(
          fmt(T('errActivityGap', 'Aktywność {0}: niespójność czasu z poprzednią.'), i + 1),
        )
        break
      }
    }
    // DST-invalid-time check (matches WPF IsInvalidTime in
    // SuperTacho.DataModel.Helpers.DateTimeExtensions).
    if (acts.length > 0) {
      const first = new Date(acts[0].startTime)
      if (isInvalidLocalTime(first)) {
        out.push(T('errInvalidDstTime', 'Początek dnia w nieistniejącym czasie (przejście DST).'))
      }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.driverId, editor.vehicleId, editor.activities])

  const buildPayload = (): UpdateDiscPayload => ({
    driverId: editor.driverId,
    vehicleId: editor.vehicleId,
    dayStartDate: editor.dayStartDate,
    dayEndDate: editor.dayEndDate,
    beginCountry: editor.beginCountry,
    endCountry: editor.endCountry,
    noonShiftMinutes: editor.noonShiftMinutes,
    firstVehicleOdometerStart: editor.firstVehicleOdometerStart,
    firstVehicleOdometerEnd: editor.firstVehicleOdometerEnd,
    distanceFromChart: editor.distanceFromChart,
    tachographStatus: editor.tachographStatus,
    isCrewWork: editor.isCrewWork,
    isClosed: editor.isClosed,
    addManualEntryOnDayStart: editor.addManualEntryOnDayStart,
    addManualEntryOnDayEnd: editor.addManualEntryOnDayEnd,
    dayNote: editor.dayNote,
    violations: editor.violations,
    activities: editor.activities.map((a) => ({
      startTime: a.startTime,
      endTime: a.endTime,
      type: a.type,
      manualEntry: a.manualEntry,
      ferryTrainCrossing: a.ferryTrainCrossing,
      crew: a.crew,
    })),
    additionalVehiclesUsed: editor.additionalVehiclesUsed.map((v) => ({
      vehicleId: v.vehicleId,
      startTime: v.startTime,
      endTime: v.endTime,
      odometerStart: v.odometerStart,
      odometerEnd: v.odometerEnd,
      distanceFromChart: v.distanceFromChart,
    })),
    countryChanges: editor.countryChanges.map((c) => ({
      country: c.country,
      previousCountry: c.previousCountry,
      atTime: c.atTime,
    })),
    // Edited distance points (shift+click add / click remove). Server wipes-and-
    // rewrites the TachographChartDistancePointEntity rows, then recomputes the
    // per-vehicle distance from the new set.
    distanceData: editor.distanceData,
  })

  // When the server returns a conflict list, open a confirm dialog and let
  // the user decide whether to overwrite.
  const [conflicts, setConflicts] = React.useState<UpdateDiscConflict[] | null>(null)
  const [pendingThen, setPendingThen] = React.useState<(() => void) | null>(null)

  const persist = async (then?: () => void) => {
    setSaving(true)
    try {
      const resp = await onSave({ ...buildPayload(), force: false })
      if (!resp.saved && resp.conflicts.length > 0) {
        setConflicts(resp.conflicts)
        setPendingThen(() => then ?? (() => undefined))
        return
      }
      if (resp.saved && resp.warnings && resp.warnings.length > 0) {
        // Non-blocking: surface inactive-vehicle / similar warnings inline so the
        // user knows the chart was saved but flagged.
        alert(resp.warnings.join('\n'))
      }
      then?.()
    } finally {
      setSaving(false)
    }
  }

  const persistForced = async () => {
    setSaving(true)
    try {
      const resp = await onSave({ ...buildPayload(), force: true })
      if (resp.saved) pendingThen?.()
    } finally {
      setConflicts(null)
      setPendingThen(null)
      setSaving(false)
    }
  }

  return (
    <div className="tw-flex tw-h-[88vh] tw-flex-col">
      <header className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200 tw-px-4 tw-py-2">
        <div>
          <h2 className="tw-text-base tw-font-semibold tw-text-slate-900">
            {fmt(T('discOfCount', 'Tarczka {0} z {1}'), discIndex + 1, discCount)}
          </h2>
          <p className="tw-text-xs tw-text-slate-500">
            {fmt(
              T('discGeometry', 'Środek: ({0}, {1}) · Kąt: {2}°'),
              disc.centerX,
              disc.centerY,
              disc.angleDegrees.toFixed(1),
            )}
          </p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          {onReassign && (
            <button
              onClick={onReassign}
              title={T(
                'editAssignmentHint',
                'Wróć do przypisania kierowcy / pojazdu / daty dla skanu',
              )}
              className="tw-rounded tw-border tw-border-slate-300 tw-px-3 tw-py-1 tw-text-sm"
            >
              {T('editAssignment', 'Edytuj przypisanie')}
            </button>
          )}
          <button
            onClick={onPrev}
            disabled={!hasPrev}
            className="tw-rounded tw-border tw-border-slate-300 tw-px-3 tw-py-1 tw-text-sm disabled:tw-opacity-40"
          >
            {T('prevDisc', '← Poprzednia')}
          </button>
          <button
            onClick={onNext}
            disabled={!hasNext}
            className="tw-rounded tw-border tw-border-slate-300 tw-px-3 tw-py-1 tw-text-sm disabled:tw-opacity-40"
          >
            {T('nextDisc', 'Następna →')}
          </button>
        </div>
      </header>

      <div className="tw-flex tw-flex-1 tw-gap-3 tw-overflow-hidden tw-p-3">
        {/* COL 1: form sidebar (violations, day fields, summary). Narrower than
            before — the activity table moved into its own column so the form
            inputs no longer compete with the wide DataGrid for width. */}
        <aside className="tw-flex tw-w-[300px] tw-flex-shrink-0 tw-flex-col tw-gap-3 tw-overflow-y-auto">
          <ViolationsExpander
            violations={editor.violations}
            dayNote={editor.dayNote}
            onChangeViolation={editor.setViolation}
            onChangeDayNote={editor.setDayNote}
          />
          <ChartFormSidebar companyId={companyId} editor={editor} />
          <ChartSummary
            activities={editor.activities}
            dayStartDate={editor.dayStartDate}
            dayEndDate={editor.dayEndDate}
          />
        </aside>

        {/* COL 2: activity DataGrid. Matches WPF: bottom-left of the import view
            is the activities table. We give it its own ~440 px column so the 6
            data columns (icon, from, to, duration, ferry, vehicle change) all
            fit on one line without header wrap. */}
        <section className="tw-flex tw-w-[440px] tw-flex-shrink-0 tw-flex-col tw-gap-2 tw-overflow-hidden">
          <ActivityListTable
            activities={editor.activities}
            noonShiftMinutes={editor.noonShiftMinutes}
            hoveredActivityId={editor.hoveredActivityId}
            hoverFromCanvas={editor.hoverFromCanvas}
            selectedActivityIds={editor.selectedActivityIds}
            onHover={(id) => editor.setHovered(id, false)}
            onSelect={(ids) => editor.setSelected(ids)}
            onContextMenu={contextMenu.open}
            vehicleChangeAtIso={
              new Map(
                editor.additionalVehiclesUsed.map((v) => [
                  v.startTime,
                  v.vehicleId
                    ? fmt(T('vehicleChangeTo', 'Zmiana → #{0}'), v.vehicleId)
                    : T('vehicleChangeShort', 'Zmiana'),
                ]),
              )
            }
          />
        </section>

        {/* CENTER: toolbar + canvas + footer */}
        <main className="tw-flex tw-flex-1 tw-flex-col tw-gap-2 tw-overflow-hidden">
          <ChartImportToolbar
            addingMode={addingMode}
            onChangeMode={setAddingMode}
            canUndo={editor.canUndo}
            canRedo={editor.canRedo}
            onUndo={editor.undo}
            onRedo={editor.redo}
            zoom={zoom}
            onZoomIn={() => setZoom((z) => Math.min(5, z + 0.25))}
            onZoomOut={() => setZoom((z) => Math.max(1, z - 0.25))}
            showDistance={showDistance}
            onToggleDistance={() => setShowDistance((v) => !v)}
            compactView={compactView}
            onToggleCompactView={() => setCompactView((v) => !v)}
            onRefresh={async () => {
              const r = await redetectChartDisc(companyId, disc.discId)
              editor.replaceActivities(r.data)
            }}
          />
          <div
            ref={canvasWrapRef}
            // White background — matches WPF's TachographChartControl which sits on
            // the parent window's white surface (Cut() pre-fills with Color.White).
            // Anything outside the chart image stays white so panning/zooming doesn't
            // reveal a gray "void".
            className="tw-relative tw-flex-1 tw-overflow-hidden tw-rounded tw-border tw-border-slate-200 tw-bg-white"
          >
            {showDistance && (
              <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-2 tw-z-10 tw--translate-x-1/2 tw-rounded tw-bg-slate-800/80 tw-px-3 tw-py-1 tw-text-center tw-text-xs tw-text-white tw-shadow">
                {T(
                  'distancePointsHint',
                  'Shift + kliknięcie na okręgu dodaje punkt dystansu • kliknięcie punktu usuwa go',
                )}
              </div>
            )}
            <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
              {imageUrl ? (
                <TachographChartCanvas
                  imageUrl={imageUrl}
                  disc={disc}
                  activities={editor.activities}
                  onActivitiesChange={(next, sameDrag) => {
                    // Junction-drag updates only the activities array. Use the
                    // editor's `replaceActivities` (which goes through mutate →
                    // push) so the rest of the state (additionalVehiclesUsed,
                    // countryChanges, violations, …) is preserved verbatim. The
                    // earlier hand-written `reset` was missing these fields and
                    // crashed downstream consumers (vehicleChangeMinutes useMemo)
                    // the moment a boundary was dragged.
                    editor.replaceActivities(next, sameDrag)
                  }}
                  hoveredActivityId={editor.hoveredActivityId}
                  selectedActivityIds={editor.selectedActivityIds}
                  onHoverActivity={(id, fromCanvas) => editor.setHovered(id, fromCanvas)}
                  onSelectActivity={(ids, shift, ctrl) => {
                    // Shift/Ctrl extends selection; plain click replaces.
                    if (shift || ctrl) {
                      const next = new Set(editor.selectedActivityIds)
                      for (const id of ids) {
                        if (next.has(id)) next.delete(id)
                        else next.add(id)
                      }
                      editor.setSelected(Array.from(next))
                    } else {
                      editor.setSelected(ids)
                    }
                  }}
                  onContextMenuActivity={contextMenu.open}
                  startBoundaryMinute={dayStartMin}
                  endBoundaryMinute={dayEndMin}
                  noonShiftMinutes={editor.noonShiftMinutes}
                  onChangeStartBoundary={(min) =>
                    editor.setDayStartTime(minToHHMM(min))
                  }
                  onChangeEndBoundary={(min) => editor.setDayEndTime(minToHHMM(min))}
                  onChangeNoonShift={editor.setNoonShiftMinutes}
                  onCursorTimeChange={setCursorMinute}
                  onClickEmptyArea={(min) => {
                    // Only add if the user has selected an activity-type mode.
                    // Without an active mode, clicks on the chart do nothing
                    // (matches WPF — toolbar buttons are radio-like, none on
                    // by default).
                    if (addingMode != null) editor.addActivityAtMinute(addingMode, min)
                  }}
                  beginCountryCode={markers.beginCountryCode}
                  endCountryCode={markers.endCountryCode}
                  distanceData={editor.distanceData}
                  showDistance={showDistance}
                  onAddDistancePoint={(kind, p) => editor.addDistancePoint(kind, p)}
                  onRemoveDistancePoint={(kind, i) => editor.removeDistancePoint(kind, i)}
                  compactView={compactView}
                  vehicleChangeMinutes={markers.vehicleChangeMinutes}
                  countryChanges={markers.countryChanges}
                  width={canvasSize}
                  height={canvasSize}
                  zoom={zoom}
                  onZoomChange={setZoom}
                />
              ) : (
                <div className="tw-text-sm tw-text-slate-400">
                  {T('loadingImage', 'Wczytywanie obrazu...')}
                </div>
              )}
            </div>
          </div>
          <ChartEditorFooter
            addManualEntryOnDayStart={editor.addManualEntryOnDayStart}
            addManualEntryOnDayEnd={editor.addManualEntryOnDayEnd}
            cursorMinute={cursorMinute}
            noonShiftMinutes={editor.noonShiftMinutes}
            onChangeAddManualEntryOnDayStart={editor.setAddManualEntryOnDayStart}
            onChangeAddManualEntryOnDayEnd={editor.setAddManualEntryOnDayEnd}
            onChangeNoonShift={editor.setNoonShiftMinutes}
          />
        </main>
      </div>

      <footer className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-slate-200 tw-px-4 tw-py-3">
        <button
          onClick={async () => {
            if (!confirm(T('confirmDeleteDisc', 'Usunąć tę tarczkę z importu?'))) return
            await onDelete()
          }}
          className="tw-rounded tw-border tw-border-red-300 tw-px-3 tw-py-1.5 tw-text-sm tw-text-red-700 hover:tw-bg-red-50"
        >
          {T('deleteDisc', 'Usuń tarczkę')}
        </button>
        {errors.length > 0 && (
          <ul className="tw-flex-1 tw-text-xs tw-text-red-600">
            {errors.map((m, i) => (
              <li key={i}>• {m}</li>
            ))}
          </ul>
        )}
        <div className="tw-flex tw-gap-2">
          <button
            onClick={() => setDiagnosticOpen(true)}
            className="tw-rounded tw-border tw-border-purple-300 tw-px-3 tw-py-1.5 tw-text-sm tw-text-purple-700 hover:tw-bg-purple-50"
            title={T('diagnosticsHint', 'Pokaż surowe dane skanu (porównanie z WPF)')}
          >
            {T('diagnostics', 'Diagnostyka')}
          </button>
          <button
            onClick={onCancel}
            className="tw-rounded tw-border tw-border-slate-300 tw-px-3 tw-py-1.5 tw-text-sm"
          >
            {T('cancel', 'Anuluj')}
          </button>
          <button
            disabled={saving || errors.length > 0}
            onClick={() => persist(hasNext ? onNext : onCancel)}
            title={errors.length > 0 ? errors.join('\n') : ''}
            className="tw-rounded tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50"
          >
            {saving
              ? T('saving', 'Zapisywanie...')
              : hasNext
                ? 
                  fmt(T('saveAndNextCounter', 'Dalej {0}/{1}'), discIndex + 1, discCount)
                : T('saveAndFinish', 'Zapisz i zakończ')}
          </button>
        </div>
      </footer>

      {contextMenu.content}

      {conflicts && (
        <ConflictDialog
          conflicts={conflicts}
          onCancel={() => {
            setConflicts(null)
            setPendingThen(null)
          }}
          onConfirm={persistForced}
        />
      )}

      {diagnosticOpen && (
        <DiagnosticPanel
          companyId={companyId}
          discId={disc.discId}
          onClose={() => setDiagnosticOpen(false)}
        />
      )}
    </div>
  )
}

function isoToDayMinute(iso: string): number {
  const d = parseChartIso(iso)
  return d.getUTCHours() * 60 + d.getUTCMinutes()
}

// Detect DST-related "invalid" wall-clock times. Two flavours:
//   1. Spring-forward gap: the hour 02:00–03:00 doesn't exist on the spring
//      transition day. JS round-trip via `new Date(y,M,d,h,m)` reads back a
//      different hour — we compare to detect.
//   2. Autumn fall-back ambiguous hour: 02:00–03:00 occurs twice. JS picks
//      one but the user-entered wall time is ambiguous. Detect by checking
//      whether the same wall time exactly an hour earlier (or later) shares
//      the same UTC instant (would mean we're on the DST edge).
function isInvalidLocalTime(d: Date): boolean {
  const h = d.getHours()
  const m = d.getMinutes()
  const reconstructed = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    h,
    m,
    0,
    0,
  )
  if (reconstructed.getHours() !== h) return true
  // Ambiguous fall-back: the offset at this instant differs from the offset 1h
  // before — meaning this wall time exists twice on the calendar.
  const oneHourBefore = new Date(d.getTime() - 60 * 60 * 1000)
  if (d.getTimezoneOffset() !== oneHourBefore.getTimezoneOffset()) {
    // Only flag the AMBIGUOUS one (autumn) — spring-forward already caught above.
    if (oneHourBefore.getTimezoneOffset() < d.getTimezoneOffset()) return true
  }
  return false
}
function minToHHMM(min: number): string {
  const m = ((min % 1440) + 1440) % 1440
  const h = Math.floor(m / 60)
  return `${String(h).padStart(2, '0')}:${String(m - h * 60).padStart(2, '0')}`
}
